/* global axios */
// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.API.1..7 — dashboard REST for WhatsApp calls. Every method
// returns the axios promise; callers read `.data`. The mock layer
// (components-next/kaption/calls/mock) implements this same interface.
import ApiClient from './ApiClient';

// "09:00" ↔ "0900" (Meta's open_time / close_time).
const toMetaTime = value => String(value || '').replace(':', '');
const fromMetaTime = value => {
  const digits = String(value || '').replace(':', '');
  return digits.length === 4 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : '';
};

// whatsapp-calling.API.9 → the settings tab's shape.
// whatsapp-calling-ownership.LINE.2 — the line fields the settings tab edits.
export const LINE_FIELDS = [
  'designated_user_id',
  'backup_user_ids',
  'ring_assignee_first',
  'ring_seconds_per_person',
  'on_no_answer',
  'outbound_policy',
  // NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ai.ANSWER.1 — who answers calls.
  'answer_mode',
];

export const DEFAULT_LINE = Object.freeze({
  designated_user_id: null,
  backup_user_ids: [],
  ring_assignee_first: true,
  ring_seconds_per_person: 20,
  on_no_answer: 'assign_designated',
  outbound_policy: 'assignee',
  answer_mode: 'people_only',
});

const pickLineFields = (line = {}) =>
  LINE_FIELDS.reduce((body, key) => {
    if (line[key] !== undefined) body[key] = line[key];
    return body;
  }, {});

// whatsapp-call-transcripts.REC.UI — the inbox "Recording" section. The
// notice mode: 'auto' (played when the call starts), 'button' (agents get a
// "Play recording notice" button), 'agent' (agents say it themselves).
export const RECORDING_NOTICE_MODES = ['auto', 'button', 'agent'];
export const RECORDING_FIELDS = [
  'enabled',
  'transcribe',
  'notice_mode',
  'notice_language',
  'retention_days',
];
export const DEFAULT_RECORDING = Object.freeze({
  enabled: false,
  transcribe: true,
  notice_mode: 'auto',
  notice_language: 'es',
  retention_days: 90,
  notice_preview_url: null,
});

// What the customer hears, per notice language — the pre-recorded prompt's
// script (transcripts plan §3.6; wording pending legal review).
export const NOTICE_SCRIPTS = Object.freeze({
  es: 'Esta llamada puede ser grabada y transcripta con fines de calidad y capacitación.',
  pt: 'Esta chamada pode ser gravada e transcrita para fins de qualidade e treinamento.',
  en: 'This call may be recorded and transcribed for quality and training purposes.',
});

export const fromBackendRecording = (recording = {}) => ({
  ...DEFAULT_RECORDING,
  ...(recording || {}),
});

const pickRecordingFields = (recording = {}) =>
  RECORDING_FIELDS.reduce((body, key) => {
    if (recording[key] !== undefined) body[key] = recording[key];
    return body;
  }, {});

export const fromBackendCallingSettings = (data = {}) => {
  const hours = data?.call_hours || null;
  return {
    recording: fromBackendRecording(data?.recording),
    // whatsapp-calling-ownership.LINE.2 — the full line JSON (contract map).
    line: { ...DEFAULT_LINE, ...(data?.line || {}) },
    enabled: data?.status === 'enabled',
    eligible: data?.eligible !== false,
    coexistence: !!data?.coexistence,
    call_icon_visibility: data?.call_icon_visibility || 'DEFAULT',
    // whatsapp-calling-ai.UI.1 — the inbox's Studio Chat assistant (read-only).
    assistant_feature_enabled: !!data?.assistant_feature_enabled,
    assistant: data?.assistant || null,
    call_hours: hours
      ? {
          enabled: hours.status === 'ENABLED',
          timezone: hours.timezone_id,
          weekly: (hours.weekly_operating_hours || []).map(row => ({
            day: row.day_of_week,
            open: fromMetaTime(row.open_time),
            close: fromMetaTime(row.close_time),
          })),
        }
      : { enabled: false },
  };
};

// The settings tab's shape → POST body (Meta's call_hours, passed through).
export const toBackendCallingSettings = (settings = {}) => {
  const hours = settings.call_hours || {};
  const body = {
    call_icon_visibility: settings.call_icon_visibility || 'DEFAULT',
    // whatsapp-calling-ownership.LINE.1 — enabling carries the line.
    ...pickLineFields(settings.line),
  };
  if (hours.enabled) {
    body.call_hours = {
      status: 'ENABLED',
      timezone_id: hours.timezone,
      weekly_operating_hours: (hours.weekly || []).map(row => ({
        day_of_week: row.day,
        open_time: toMetaTime(row.open),
        close_time: toMetaTime(row.close),
      })),
    };
  }
  return body;
};

class KaptionCallsAPI extends ApiClient {
  constructor() {
    super('kaption/calls', { accountScoped: true });
  }

  // whatsapp-calling.API.1 — the Calls workspace list.
  list({ status, direction, page } = {}) {
    return axios.get(this.url, { params: { status, direction, page } });
  }

  // whatsapp-calling.API.1 — one call with its conversation.
  show(id) {
    return axios.get(`${this.url}/${id}`);
  }

  // whatsapp-calling.API.2 — {bridge_url, participant_token, ice_servers} | 409.
  accept(id, { device }) {
    return axios.post(`${this.url}/${id}/accept`, { device });
  }

  // whatsapp-calling.API.3. `forwardToPhone` is the desktop "Send to my
  // phone" action (DESKTOP.1 / ROUTING.7): not a decline — only this agent's
  // phones keep ringing.
  decline(id, { device, forwardToPhone } = {}) {
    const body = { device };
    if (forwardToPhone) body.forward_to_phone = true;
    return axios.post(`${this.url}/${id}/decline`, body);
  }

  end(id) {
    return axios.post(`${this.url}/${id}/end`);
  }

  // whatsapp-calling-ownership.PASS.1 — teammates for the Pass call picker.
  passTargets(id) {
    return axios.get(`${this.url}/${id}/pass_targets`);
  }

  // whatsapp-calling-ownership.PASS.2 → 202 {pass}.
  pass(id, body) {
    return axios.post(`${this.url}/${id}/pass`, body);
  }

  // whatsapp-calling-ownership.PASS.3..6 — target: accept / decline (also
  // "Leave" during a consult) / unmuted; passer: complete / cancel / undo.
  acceptPass(id, passId, { device }) {
    return axios.post(`${this.url}/${id}/pass/${passId}/accept`, { device });
  }

  declinePass(id, passId) {
    return axios.post(`${this.url}/${id}/pass/${passId}/decline`);
  }

  completePass(id, passId) {
    return axios.post(`${this.url}/${id}/pass/${passId}/complete`);
  }

  cancelPass(id, passId) {
    return axios.post(`${this.url}/${id}/pass/${passId}/cancel`);
  }

  undoPass(id, passId) {
    return axios.post(`${this.url}/${id}/pass/${passId}/undo`);
  }

  passUnmuted(id, passId) {
    return axios.post(`${this.url}/${id}/pass/${passId}/unmuted`);
  }

  // whatsapp-calling.API.4 — accept payload | {status: 'permission_required'}.
  // whatsapp-calling-ownership.OUTBOUND.1 — who may call is the server's
  // decision; there is no take-over flag any more.
  startOutbound(conversationId, { device } = {}) {
    return axios.post(
      `${this.baseUrl()}/conversations/${conversationId}/kaption_calls`,
      { device }
    );
  }

  // whatsapp-calling-ownership.OUTBOUND.5 → {can_call, reason, assigns, assignee}.
  outboundEligibility(conversationId) {
    return axios.get(
      `${this.baseUrl()}/conversations/${conversationId}/kaption_calls/eligibility`
    );
  }

  // whatsapp-calling.API.12 / WEB.9 — this device has no mic or no session.
  mediaFailed(id, { device, reason }) {
    return axios.post(`${this.url}/${id}/media_failed`, { device, reason });
  }

  // whatsapp-calling-ownership.LINE.3 — lines I designate, hold or back up.
  getCallLines() {
    return axios.get(`${this.baseUrl()}/kaption/call_lines`);
  }

  // until: 'until_taken_back' | 'end_of_day' | unix seconds. Resolves to the
  // API.9 shape (with `line`).
  passLine(inboxId, { userId, until }) {
    return axios.post(`${this.inboxCallingUrl(inboxId)}/line_holder`, {
      user_id: userId,
      until,
    });
  }

  takeLineBack(inboxId) {
    return axios.delete(`${this.inboxCallingUrl(inboxId)}/line_holder`);
  }

  // whatsapp-calling.API.5
  requestPermission(conversationId) {
    return axios.post(
      `${this.baseUrl()}/conversations/${conversationId}/kaption_calls/permission_request`
    );
  }

  // whatsapp-calling.API.6
  getAvailability() {
    return axios.get(`${this.baseUrl()}/kaption/call_availability`);
  }

  setAvailability(availability) {
    return axios.patch(`${this.baseUrl()}/kaption/call_availability`, {
      availability,
    });
  }

  // whatsapp-calling.API.9 / META.7 / WEB.6 — the backend speaks Meta's
  // calling settings (status, call_hours.weekly_operating_hours, "HHMM"); the
  // settings tab speaks {enabled, call_hours: {enabled, timezone, weekly:
  // [{day, open, close}]}}. The mapping lives here so the tab and the mock
  // layer keep one shape. Both methods resolve to {data: <tab shape>}.
  async getInboxCallingSettings(inboxId) {
    const response = await axios.get(this.inboxCallingUrl(inboxId));
    return { ...response, data: fromBackendCallingSettings(response.data) };
  }

  // Enabling POSTs (admin, with the line — LINE.1), disabling saves the line
  // with PATCH (no Meta call) and then DELETEs (admin).
  async updateInboxCallingSettings(inboxId, settings = {}) {
    if (settings.enabled) {
      const response = await axios.post(
        this.inboxCallingUrl(inboxId),
        toBackendCallingSettings(settings)
      );
      return { ...response, data: fromBackendCallingSettings(response.data) };
    }
    if (settings.line) await this.updateInboxLine(inboxId, settings.line);
    const response = await axios.delete(this.inboxCallingUrl(inboxId));
    return { ...response, data: fromBackendCallingSettings(response.data) };
  }

  // whatsapp-calling-ai.SUMMARY.4 — ask Studio Chat for a new call summary.
  regenerateAiSummary(id) {
    return axios.post(`${this.url}/${id}/ai_summary/regenerate`);
  }

  // whatsapp-calling-ownership.LINE.2 — PATCH the line only (no Meta call).
  async updateInboxLine(inboxId, line = {}) {
    const response = await axios.patch(
      this.inboxCallingUrl(inboxId),
      pickLineFields(line)
    );
    return { ...response, data: fromBackendCallingSettings(response.data) };
  }

  // whatsapp-calling-ownership.UI.1 / UI.2 — the people who can be designated,
  // backups or line holders (inbox members, with availability_status).
  getInboxMembers(inboxId) {
    return axios.get(`${this.baseUrl()}/inbox_members/${inboxId}`);
  }

  // whatsapp-call-transcripts.REC.UI — PATCH the Recording section only (no
  // Meta call). Resolves to the settings tab shape.
  async updateInboxRecording(inboxId, recording = {}) {
    const response = await axios.patch(this.inboxCallingUrl(inboxId), {
      recording: pickRecordingFields(recording),
    });
    return { ...response, data: fromBackendCallingSettings(response.data) };
  }

  // whatsapp-call-transcripts.API.1 — {status, language, speakers, turns,
  // markers, words_coverage, duration_ms}; the reader normalises it
  // (kaptionCallTranscriptModel.normalizeTranscript).
  transcript(id) {
    return axios.get(`${this.url}/${id}/transcript`);
  }

  // whatsapp-call-transcripts.API.2 — {url, expires_at}: a short-lived
  // signed URL; 403 without the listen permission.
  recording(id) {
    return axios.get(`${this.url}/${id}/recording`);
  }

  // whatsapp-call-transcripts.API.3 — admins, failed or partial transcripts.
  retryTranscript(id) {
    return axios.post(`${this.url}/${id}/transcript/retry`);
  }

  // whatsapp-call-transcripts.REC.UI — notice mode 'button': play the
  // recorded notice to the customer now. Resolves to the call JSON.
  playRecordingNotice(id) {
    return axios.post(`${this.url}/${id}/notice`);
  }

  // whatsapp-call-transcripts.CONSENT.4 — the connected agent stops recording
  // ({on: false}). Resolves to the call JSON.
  setRecording(id, { on }) {
    return axios.post(`${this.url}/${id}/recording`, { on });
  }

  // call-transcript-search.API.1 — {query, results: [{call, contact, agents,
  // snippets: [{speaker, start_ms, text, marks}]}], meta}.
  search(params = {}) {
    return axios.get(`${this.url}/search`, { params });
  }

  inboxCallingUrl(inboxId) {
    return `${this.baseUrl()}/inboxes/${inboxId}/kaption_calling`;
  }
}

export default new KaptionCallsAPI();
